import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { from, map, Observable } from 'rxjs';

import {
  portalLead,
  portalOrder,
  portalAutoQuote,
  portalQuoteAccessState,
  setPortalAutoQuoteOptionFeedback,
  submitPortalAutoQuoteAdditionalFeedback,
  unlockPortalQuote,
  portalOptionSelected,
  portalSubmitPassengers,
  portalSetHiddenFlightsPresented,
  portalSubmitOption,
  sendPasswordToEmail,
  portalFinishPayment,
  validatePromocode,
  portalUpdateOption,
  portalTripAncillaryUpsell,
  portalTripAncillaryUpsellBanner,
  portalAncillariesUpsell,
  saveGoogleFlightsBaselineFeedback,
  portalTierCatalog,
  portalReferralCreditPreview,
} from '@app/services/api/api.graphql';
import {
  PortalPassengerNodeInput,
  PortalLeadNode,
  PortalOrderNode,
  PortalCreditCardNodeInput,
  PortalPassengerNode,
  PortalOrderStatusType,
  AncillaryUpsellNode,
  GoogleFlightsFeedbackOptionEnum,
  PortalAirlineNode,
  PortalTierEnum,
  PortalAutoQuoteNode,
  PortalAutoQuoteStatus,
  AutoQuoteOptionFeedbackEnum,
  PortalQuotePasscodeRole,
  PortalTierCatalogNode,
  PortalReferralCreditPreviewNode,
} from '@app/services/api/api.types';
import { Airport } from '@app/components/modals/planning-trip-modal/planning-trip-modal.component';
import { ErrorService } from '@app/services/error.service';
import { handleError } from '@app/operators/handle-error';
import { Option } from '@app/forms/formly/formly-utils';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class ApiService {
  constructor(
    protected httpClient: HttpClient,
    protected errorService: ErrorService,
  ) {}

  portalLead(variables: { portalLink: string }) {
    const query = portalLead();

    return this.httpClient
      .post<{ data: { portalLead: PortalLeadNode }; errors: Error[] }>(baseUrl, {
        query,
        variables,
      })
      .pipe(
        map((response) => ({
          portalLead: response.data.portalLead,
          errors: response.errors,
        })),
      );
  }

  // Load the full ``portalOrder`` for a single option (unauth, same passcode
  // gate as ``portalLead``). Used on option-select before navigating to the
  // option page so ``PortalOrderService`` holds the full checkout-shape node
  // that downstream pages read from (TIC-6859 payload split).
  portalOrder(variables: { portalLink: string }) {
    const query = portalOrder();

    return this.httpClient
      .post<{ data: { portalOrder: PortalOrderNode | null }; errors?: Error[] }>(baseUrl, {
        query,
        variables,
      })
      .pipe(
        map((response) => ({
          portalOrder: response.data?.portalOrder ?? null,
          errors: response.errors,
        })),
      );
  }

  portalTierCatalog() {
    const query = portalTierCatalog();

    return this.httpClient
      .post<{ data: { portalTierCatalog: PortalTierCatalogNode[] }; errors: Error[] }>(baseUrl, {
        query,
      })
      .pipe(map((response) => response.data.portalTierCatalog));
  }

  portalAutoQuote(variables: { token: string }): Observable<{
    portalAutoQuote: PortalAutoQuoteNode | null;
    errors?: Error[];
  }> {
    const query = portalAutoQuote();

    return this.httpClient
      .post<{ data: { portalAutoQuote: PortalAutoQuoteNode }; errors: Error[] }>(baseUrl, {
        query,
        variables,
      })
      .pipe(
        map((response) => ({
          portalAutoQuote: response.data.portalAutoQuote,
          errors: response.errors,
        })),
      );
  }

  portalQuoteAccessState(variables: { portalLink: string }) {
    const query = portalQuoteAccessState();

    return this.httpClient
      .post<{
        data: {
          portalQuoteAccessState: {
            isLocked: boolean;
          };
        };
        errors: Error[];
      }>(baseUrl, { query, variables })
      .pipe(map((response) => response.data.portalQuoteAccessState));
  }

  setPortalAutoQuoteOptionFeedback(variables: {
    input: {
      token: string;
      optionLabel: string;
      feedback: AutoQuoteOptionFeedbackEnum;
    };
  }) {
    const query = setPortalAutoQuoteOptionFeedback();

    return this.httpClient
      .post<{
        data: {
          setPortalAutoQuoteOptionFeedback: {
            ok: boolean;
            status: PortalAutoQuoteStatus;
            error: string | null;
            autoQuote: PortalAutoQuoteNode | null;
          };
        };
        errors: Error[];
      }>(baseUrl, { query, variables })
      .pipe(map((response) => response.data.setPortalAutoQuoteOptionFeedback));
  }

  submitPortalAutoQuoteAdditionalFeedback(variables: {
    input: {
      token: string;
      text: string;
    };
  }) {
    const query = submitPortalAutoQuoteAdditionalFeedback();

    return this.httpClient
      .post<{
        data: {
          submitPortalAutoQuoteAdditionalFeedback: {
            ok: boolean;
            status: PortalAutoQuoteStatus;
            error: string | null;
            autoQuote: PortalAutoQuoteNode | null;
          };
        };
        errors: Error[];
      }>(baseUrl, { query, variables })
      .pipe(map((response) => response.data.submitPortalAutoQuoteAdditionalFeedback));
  }

  unlockPortalQuote(variables: { input: { portalLink: string; passcode: string } }) {
    const query = unlockPortalQuote();

    return this.httpClient
      .post<{
        data: {
          unlockPortalQuote: {
            ok: boolean;
            error: string | null;
            token: string | null;
            role: PortalQuotePasscodeRole | null;
          };
        };
        errors: Error[];
      }>(baseUrl, { query, variables })
      .pipe(map((response) => response.data.unlockPortalQuote));
  }

  portalUpdateOption(variables: {
    inputUpdate: {
      portalLink: string;
      status: PortalOrderStatusType;
    };
  }) {
    const query = portalUpdateOption();

    return this.httpClient
      .post<{ data: { portalUpdateOption: { ok: boolean } } }>(baseUrl, { query, variables })
      .pipe(map((response) => response.data.portalUpdateOption));
  }

  portalSubmitOption(variables: {
    input: {
      portalLink: string;
      passengers: PortalPassengerNodeInput[];
      creditCard?: PortalCreditCardNodeInput;
      tipsAmount?: number;
      disruptionProtection?: boolean;
      cancelForAnyReason?: boolean;
      clientBalanceAmount?: number;
      anonymous?: boolean;
      payByStripe?: boolean;
      saveCardToStripe?: boolean;
      stripeCreditCardId?: string;
      promocode?: string;
      withBundleDiscount?: boolean;
      tier?: PortalTierEnum;
      phone?: string;
    };
  }) {
    const query = portalSubmitOption();

    return this.httpClient.post<{
      data: { portalSubmitOption: { ok: boolean; errors: string; stripeClientSecret: string } };
      errors: Error[];
    }>(cloudfrontApi, {
      query,
      variables,
    });
  }

  countries() {
    return from(import('static/countries').then((mod) => mod.default as unknown as Option[])).pipe(
      map((options) => [...options]),
      handleError(this.errorService),
    );
  }

  states() {
    return from(import('static/states').then((mod) => mod.default as unknown as Option[])).pipe(
      map((options) => [...options]),
      handleError(this.errorService),
    );
  }

  airports(): Observable<Airport[]> {
    return from(import('static/airports').then((mod) => mod.default as unknown as Airport[])).pipe(
      map((options) => [...options]),
      handleError(this.errorService),
    );
  }

  airlines(): Observable<PortalAirlineNode[]> {
    return from(
      import('static/airlines').then((mod) => mod.default as unknown as PortalAirlineNode[]),
    ).pipe(
      map((options) => [...options]),
      handleError(this.errorService),
    );
  }

  portalSubmitPassengers(variables: {
    input: { portalLink: string; passengers: PortalPassengerNodeInput[] };
  }) {
    const query = portalSubmitPassengers();

    return this.httpClient
      .post<{
        data: {
          portalSubmitPassengers: {
            ok: boolean;
            errors: string;
            passengers: PortalPassengerNode[];
          };
        };
      }>(baseUrl, {
        query,
        variables,
      })
      .pipe(map((response) => response.data.portalSubmitPassengers));
  }

  portalSetHiddenFlightsPresented(variables: {
    portalLink: string;
    hiddenFlightsPresented: boolean;
  }) {
    const query = portalSetHiddenFlightsPresented();

    return this.httpClient
      .post<{
        data: { portalSetHiddenFlightsPresented: { ok: boolean; result: PortalOrderNode } };
      }>(baseUrl, { query, variables })
      .pipe(map((response) => response.data.portalSetHiddenFlightsPresented));
  }

  sendPasswordToEmail(variables: { portalLink: string; email: string }) {
    const query = sendPasswordToEmail();

    return this.httpClient.post<{
      data: { sendPasswordToEmail: { ok: boolean } };
      errors: Error[];
    }>(baseUrl, { query, variables });
  }

  portalOptionSelected(variables: { portalLink: string }) {
    const query = portalOptionSelected();

    return this.httpClient.post<{
      data: { portalOptionSelected: { ok: boolean } };
    }>(baseUrl, { query, variables });
  }

  portalFinishPayment(variables: { portalLink: string }) {
    const query = portalFinishPayment();

    return this.httpClient.post<{
      data: { portalFinishPayment: { ok: boolean } };
    }>(baseUrl, { query, variables });
  }

  validatePromocode(variables: { input: { optionPortalLink: string; promocode: string } }) {
    const query = validatePromocode();

    return this.httpClient
      .post<{
        data: { validatePromocode: { ok: boolean; errors: string; discountAmount: number } };
      }>(baseUrl, { query, variables })
      .pipe(map((response) => response.data.validatePromocode));
  }

  specialAssistanceOptions() {
    return from(
      import('static/specialAssistanceOptions').then((mod) => mod.default as unknown as Option[]),
    ).pipe(
      map((options) => [...options]),
      handleError(this.errorService),
    );
  }

  petsOptions() {
    return from(
      import('static/petsOptions').then((mod) => mod.default as unknown as Option[]),
    ).pipe(
      map((options) => [...options]),
      handleError(this.errorService),
    );
  }

  mealPreferencesOptions() {
    return from(
      import('static/mealPreferencesOptions').then((mod) => mod.default as unknown as Option[]),
    ).pipe(
      map((options) => [...options]),
      handleError(this.errorService),
    );
  }

  seatPreferencesOptions() {
    return from(
      import('static/seatPreferencesOptions').then((mod) => mod.default as unknown as Option[]),
    ).pipe(
      map((options) => [...options]),
      handleError(this.errorService),
    );
  }

  portalTripAncillaryUpsell(variables: { tripPortalLink: string; perpetual?: boolean }) {
    const query = portalTripAncillaryUpsell();

    return this.httpClient.post<{
      data: { portalTripAncillaryUpsell: AncillaryUpsellNode };
    }>(baseUrl, { query, variables });
  }

  portalTripAncillaryUpsellBanner(variables: { tripPortalLink: string }) {
    const query = portalTripAncillaryUpsellBanner();

    return this.httpClient.post<{
      data: { portalTripAncillaryUpsell: Partial<AncillaryUpsellNode> };
    }>(baseUrl, { query, variables });
  }

  saveGoogleFlightsBaselineFeedback(variables: {
    input: {
      leadPortalLink: string;
      feedbackOption: GoogleFlightsFeedbackOptionEnum;
      budgetFrom: number;
      budgetTo: number;
      comment: string;
      routeDetails: {
        origin: string;
        destination: string;
        date: string;
        airline: string;
      };
      googlePrice: string;
      displayPrice: string;
      markdownPercentage: number;
    };
  }) {
    const query = saveGoogleFlightsBaselineFeedback();

    return this.httpClient
      .post<{
        data: {
          saveGoogleFlightsBaselineFeedback: {
            ok: boolean;
            errors: string;
            portalLead: PortalLeadNode;
          };
        };
      }>(baseUrl, { query, variables })
      .pipe(map((response) => response.data.saveGoogleFlightsBaselineFeedback));
  }

  portalAncillariesUpsell(variables: {
    input: {
      tripPortalLink: string;
      creditCard?: PortalCreditCardNodeInput;
      disruptionProtection?: boolean;
      cancelForAnyReason?: boolean;
      anonymous?: boolean;
      payByStripe?: boolean;
      withBundleDiscount?: boolean;
      saveCardToStripe?: boolean;
      stripeCreditCardId?: string;
      tipsAmount?: number;
      perpetual?: boolean;
      clientPhone?: string;
      tier?: PortalTierEnum;
    };
  }) {
    const query = portalAncillariesUpsell();

    return this.httpClient
      .post<{
        data: {
          portalAncillariesUpsell: { ok: boolean; errors: string; stripeClientSecret: string };
        };
      }>(cloudfrontApi, {
        query,
        variables,
      })
      .pipe(map((response) => response.data.portalAncillariesUpsell));
  }

  portalReferralCreditPreview(variables: {
    portalLink: string;
    optionId: string;
    passengers: PortalPassengerNodeInput[];
  }) {
    const query = portalReferralCreditPreview();

    return this.httpClient
      .post<{
        data: { portalReferralCreditPreview: PortalReferralCreditPreviewNode | null };
        errors?: { message: string }[];
      }>(baseUrl, { query, variables })
      .pipe(map((response) => response.data?.portalReferralCreditPreview ?? null));
  }
}

export const baseUrl = environment.GRAPHQL_URL;
export const cloudfrontApi = environment.CLOUDFRONT_API as string;
export const protectedBaseUrl = environment.GRAPHQL_PROTECTED_URL as string;
export const backendApiUrls = new Set([baseUrl, cloudfrontApi, protectedBaseUrl]);
